import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Button, Card, Checkbox, Empty, List, message, Modal, Radio, Space, Spin, Tag, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import axios from "axios";
import type { Device } from "./types";
import {
  buildNotificationRecipients,
  loadMimamoriNotificationSettings,
  normalizeEmail,
  recipientLabel,
  snapshotMimamoriSettings,
  WEB_REQUEST_ACTIONS,
  WEB_REQUEST_COMMAND,
  type MimamoriNotifyLevel,
  type NotificationRecipient,
} from "./NotificationSettingsShared";

const LEVELS: { value: MimamoriNotifyLevel; label: string }[] = [
  { value: "disabled", label: "なし（無効）" },
  { value: "critical", label: "重大のみ（推奨）" },
  { value: "suspicious", label: "異常の疑い時も送付" },
  { value: "all", label: "すべて通知（要介護度が高い方に推奨）" },
];

export type MimamoriSettingsEditorHandle = {
  open: () => void;
};

interface Props {
  selectedDevice: Device | null;
  className?: string;
  style?: React.CSSProperties;
  hideTrigger?: boolean;
}

const MimamoriSettingsEditor = forwardRef<MimamoriSettingsEditorHandle, Props>(({ selectedDevice, className, style, hideTrigger }, ref) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([]);
  const [notifyLevel, setNotifyLevel] = useState<MimamoriNotifyLevel>("disabled");
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [loadedSnapshot, setLoadedSnapshot] = useState("");
  const loadSeqRef = useRef(0);
  const openedDeviceIdRef = useRef<string | null>(null);

  const isDirty = snapshotMimamoriSettings(notifyLevel, selectedEmails) !== loadedSnapshot;

  const loadEditorData = useCallback(async () => {
    if (!selectedDevice) {
      setRecipients([]);
      return;
    }
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const settings = await loadMimamoriNotificationSettings(selectedDevice.id);
      if (seq !== loadSeqRef.current) return;

      const nextRecipients = buildNotificationRecipients(
        selectedDevice.managerEmail,
        selectedDevice.familyEmails
      );
      const allowed = new Set(nextRecipients.map((x) => x.email));
      const nextSelected = settings.selectedEmails.filter((email) => allowed.has(email));
      setRecipients(nextRecipients);
      setNotifyLevel(settings.notifyLevel);
      setSelectedEmails(nextSelected);
      setLoadedSnapshot(snapshotMimamoriSettings(settings.notifyLevel, nextSelected));
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      console.error("みまもり設定の取得に失敗:", e);
      message.error("みまもり設定の取得に失敗しました。");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [selectedDevice]);

  const closeEditor = () => {
    loadSeqRef.current += 1;
    setEditorOpen(false);
    setRecipients([]);
    setNotifyLevel("disabled");
    setSelectedEmails([]);
    setLoadedSnapshot("");
    setLoading(false);
  };

  const handleOpenEditor = () => {
    if (!selectedDevice) {
      message.warning("デバイスを選択してください。");
      return;
    }
    openedDeviceIdRef.current = selectedDevice.id;
    setRecipients([]);
    setNotifyLevel("disabled");
    setSelectedEmails([]);
    setLoadedSnapshot("");
    setEditorOpen(true);
    void loadEditorData();
  };

  useImperativeHandle(ref, () => ({ open: handleOpenEditor }), [selectedDevice]);

  useEffect(() => {
    if (!editorOpen) return;
    if (selectedDevice?.id === openedDeviceIdRef.current) return;
    closeEditor();
  }, [editorOpen, selectedDevice?.id]);

  const handleCloseEditor = async () => {
    if (isDirty) {
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: "未送信の変更があります",
          content: "タブレットへ未送信の編集内容があります。破棄して閉じますか？",
          okText: "破棄して閉じる",
          cancelText: "キャンセル",
          okButtonProps: { danger: true },
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
    }
    closeEditor();
  };

  const toggleEmail = (email: string, checked: boolean) => {
    setSelectedEmails((prev) =>
      checked ? (prev.includes(email) ? prev : [...prev, email]) : prev.filter((x) => x !== email)
    );
  };

  const handleSendToTablet = async () => {
    if (!selectedDevice?.fcmToken) {
      message.warning("FCMトークンが未設定のため、タブレットへ送信できません。");
      return;
    }
    const payload = {
      notifyLevel,
      selectedEmails: selectedEmails.map(normalizeEmail).filter(Boolean),
    };
    setSending(true);
    try {
      const requestId = crypto.randomUUID();
      await axios.post("/api/sendFCM", {
        tokens: [selectedDevice.fcmToken],
        payload: {
          mode: "request",
          command: WEB_REQUEST_COMMAND,
          param1: WEB_REQUEST_ACTIONS.mimamori,
          param2: JSON.stringify(payload),
          param3: requestId,
          param4: String(Date.now()),
        },
      });
      message.success("タブレットに送信しました");
      closeEditor();
    } catch (e) {
      console.error("FCM送信エラー:", e);
      message.error("タブレットへの送信に失敗しました。");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
      <div className={className} style={{ display: "inline-block", ...style }}>
        <Button type="primary" onClick={handleOpenEditor} disabled={!selectedDevice}>
          みまもり設定
        </Button>
      </div>
      )}

      <Modal
        title={
          <span>
            <EyeOutlined style={{ marginRight: 8, color: "#4f6ef7" }} />
            みまもり設定 - {selectedDevice?.deviceName || selectedDevice?.serialNumber}
          </span>
        }
        open={editorOpen}
        onCancel={() => void handleCloseEditor()}
        width={720}
        centered
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => void handleCloseEditor()}>
            閉じる
          </Button>,
          <Button key="send" type="primary" loading={sending} onClick={() => void handleSendToTablet()} disabled={!selectedDevice || loading}>
            タブレットへ送信して反映
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          みまもりの通知レベルと通知先を設定します。送信しないで閉じると、変更は破棄されます。
        </Typography.Paragraph>

        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Typography.Text strong>自動みまもりの通知レベル</Typography.Text>
            <Radio.Group
              value={notifyLevel}
              onChange={(e) => setNotifyLevel(e.target.value as MimamoriNotifyLevel)}
              style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8, marginBottom: 16 }}
            >
              {LEVELS.map((item) => (
                <Radio key={item.value} value={item.value}>
                  {item.label}
                </Radio>
              ))}
            </Radio.Group>

            <Typography.Text strong>通知先メール</Typography.Text>
            <div style={{ marginTop: 4, marginBottom: 8 }}>現在の選択: {selectedEmails.length}件</div>
            {recipients.length === 0 ? (
              <Empty description="送信先候補がありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                dataSource={recipients}
                renderItem={(item) => (
                  <List.Item>
                    <Checkbox
                      checked={notifyLevel !== "disabled" && selectedEmails.includes(item.email)}
                      disabled={notifyLevel === "disabled"}
                      onChange={(e) => toggleEmail(item.email, e.target.checked)}
                    >
                      <Space wrap>
                        <Tag color={item.isManager ? "gold" : "blue"}>{recipientLabel(item)}</Tag>
                        <Typography.Text>{item.email}</Typography.Text>
                      </Space>
                    </Checkbox>
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
});

MimamoriSettingsEditor.displayName = "MimamoriSettingsEditor";

export default MimamoriSettingsEditor;
